import { ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class ProductUpsellItemComponent extends HTMLElement {
  /** @type {string} */
  #productId = '';

  /** @type {string} */
  #currentProductId = '';

  /** @type {string} */
  #productHandle = '';

  /** @type {string} */
  #currentProductHandle = '';

  /** @type {string} */
  #productTitle = '';

  /** @type {string} */
  #currentProductTitle = '';

  /** @type {string} */
  #variantId = '';

  /** @type {string} */
  #sourceId = '';

  /** @type {boolean} */
  #pending = false;

  /** @type {number} */
  #quantity = 1;

  /** @type {number | null} */
  #syncTimeout = null;

  /** @type {number | null} */
  #quantityDebounceTimer = null;

  /** @type {boolean} */
  #pendingRemove = false;

  connectedCallback() {
    this.#productId = this.dataset.productId || '';
    this.#currentProductId = this.dataset.currentProductId || this.#getCurrentProductIdFromPage();
    this.#productHandle = this.dataset.productHandle || '';
    this.#currentProductHandle = this.dataset.currentProductHandle || this.#getCurrentProductHandleFromPage();
    this.#productTitle = this.dataset.productTitle || '';
    this.#currentProductTitle = this.dataset.currentProductTitle || this.#getCurrentProductTitleFromPage();
    this.#variantId = this.dataset.variantId || '';
    this.#sourceId = this.id || `product-upsell-item-${this.dataset.blockId || this.#variantId}`;
    this.#quantity = Number(this.dataset.quantity) || 1;

    if (this.#shouldHideCurrentProductUpsell()) {
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      return;
    }

    if (!this.#variantId) return;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.addEventListener('click', this.#onCardClick);

    const minusBtn = this.querySelector('[data-action="minus"]');
    const plusBtn = this.querySelector('[data-action="plus"]');
    if (minusBtn) minusBtn.addEventListener('click', this.#onQuantityMinus);
    if (plusBtn) plusBtn.addEventListener('click', this.#onQuantityPlus);

    this.#syncFromCart();
  }

  #shouldHideCurrentProductUpsell() {
    const upsellProductId = this.#normalizeId(this.#productId);
    const currentProductId = this.#normalizeId(this.#currentProductId);
    const upsellProductHandle = this.#normalizeText(this.#productHandle);
    const currentProductHandle = this.#normalizeText(this.#currentProductHandle);
    const upsellProductTitle = this.#normalizeText(this.#productTitle);
    const currentProductTitle = this.#normalizeText(this.#currentProductTitle);

    if (upsellProductId && currentProductId && upsellProductId === currentProductId) {
      return true;
    }

    if (upsellProductHandle && currentProductHandle && upsellProductHandle === currentProductHandle) {
      return true;
    }

    if (upsellProductTitle && currentProductTitle && upsellProductTitle === currentProductTitle) {
      return true;
    }

    return false;
  }

  #normalizeId(value) {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  #normalizeText(value) {
    if (value === null || value === undefined) return '';
    return String(value).trim().toLowerCase();
  }

  #getCurrentProductIdFromPage() {
    const sources = [
      this.ownerDocument?.querySelector('product-information[data-product-id]')?.dataset?.productId,
      this.ownerDocument?.querySelector('sticky-add-to-cart[data-product-id]')?.dataset?.productId,
      this.ownerDocument?.querySelector('product-form-component[data-product-id]')?.dataset?.productId,
      this.ownerDocument?.querySelector('variant-picker[data-product-id]')?.dataset?.productId,
      window?.ShopifyAnalytics?.meta?.product?.id,
      window?.meta?.product?.id,
    ];

    for (const source of sources) {
      const normalizedValue = this.#normalizeId(source);
      if (normalizedValue) return normalizedValue;
    }

    return '';
  }

  #getCurrentProductHandleFromPage() {
    const pathHandle = window.location.pathname.match(/^\/products\/([^/]+)/)?.[1];
    const sources = [window?.ShopifyAnalytics?.meta?.product?.handle, window?.meta?.product?.handle, pathHandle];

    for (const source of sources) {
      const normalizedValue = this.#normalizeText(source);
      if (normalizedValue) return normalizedValue;
    }

    return '';
  }

  #getCurrentProductTitleFromPage() {
    const sources = [
      window?.ShopifyAnalytics?.meta?.product?.title,
      window?.meta?.product?.title,
      this.ownerDocument?.querySelector('.product-details .view-product-title a')?.textContent,
      this.ownerDocument?.querySelector('.product-details h1')?.textContent,
      this.ownerDocument?.querySelector('h1')?.textContent,
    ];

    for (const source of sources) {
      const normalizedValue = this.#normalizeText(source);
      if (normalizedValue) return normalizedValue;
    }

    return '';
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.removeEventListener('click', this.#onCardClick);

    const minusBtn = this.querySelector('[data-action="minus"]');
    const plusBtn = this.querySelector('[data-action="plus"]');
    if (minusBtn) minusBtn.removeEventListener('click', this.#onQuantityMinus);
    if (plusBtn) plusBtn.removeEventListener('click', this.#onQuantityPlus);

    if (this.#syncTimeout) {
      window.clearTimeout(this.#syncTimeout);
      this.#syncTimeout = null;
    }

    if (this.#quantityDebounceTimer) {
      window.clearTimeout(this.#quantityDebounceTimer);
      this.#quantityDebounceTimer = null;
    }
  }

  #onCardClick = () => {
    if (this.#isChecked() || this.#pending) return;
    this.#addToCart();
  };

  /** @param {MouseEvent} event */
  #onQuantityMinus = (event) => {
    event.stopPropagation();
    if (this.#quantity <= 0) return;
    this.#quantity -= 1;
    if (this.#quantity === 0) {
      this.#setChecked(false);
      this.#quantity = 1; // reset for next appearance, never render 0
      this.#pendingRemove = true;
    } else {
      this.#pendingRemove = false;
      this.#updateQuantityDisplay();
    }
    this.#scheduleQuantityUpdate();
  };

  /** @param {MouseEvent} event */
  #onQuantityPlus = (event) => {
    event.stopPropagation();
    this.#pendingRemove = false;
    this.#quantity += 1;
    this.#updateQuantityDisplay();
    this.#scheduleQuantityUpdate();
  };

  #scheduleQuantityUpdate() {
    if (this.#quantityDebounceTimer) window.clearTimeout(this.#quantityDebounceTimer);
    this.#quantityDebounceTimer = window.setTimeout(async () => {
      this.#quantityDebounceTimer = null;
      if (this.#pending) {
        this.#scheduleQuantityUpdate();
        return;
      }
      if (this.#pendingRemove) {
        this.#pendingRemove = false;
        this.#setPending(true);
        try {
          await this.#removeFromCart();
        } catch (error) {
          console.error('Upsell remove failed:', error);
          await this.#syncFromCart();
        } finally {
          this.#setPending(false);
        }
      } else {
        await this.#changeQuantity(this.#quantity);
      }
    }, 400);
  }

  /** @param {CustomEvent} event */
  #onCartUpdate = (event) => {
    if (event?.detail?.sourceId === this.#sourceId) return;

    const cartItems = event?.detail?.resource?.items;
    if (Array.isArray(cartItems)) {
      this.#syncCheckedStateFromCart(/** @type {{items: Array<{variant_id: number | string, quantity: number}>}} */ (event.detail.resource));
      return;
    }

    if (this.#syncTimeout) {
      window.clearTimeout(this.#syncTimeout);
    }

    this.#syncTimeout = window.setTimeout(() => {
      this.#syncFromCart();
      this.#syncTimeout = null;
    }, 100);
  };

  async #addToCart() {
    if (!this.#variantId || this.#pending) return;

    this.#setChecked(true);
    this.#setPending(true);

    try {
      await this.#addVariantToCart();
    } catch (error) {
      console.error('Upsell add failed:', error);
      this.#setChecked(false);
      await this.#syncFromCart();
    } finally {
      this.#setPending(false);
    }
  }

  async #addVariantToCart() {
    const formData = new FormData();
    const sections = this.#getSectionIds();

    formData.append('id', this.#variantId);
    formData.append('quantity', '1');
    if (sections) formData.append('sections', sections);
    formData.append('sections_url', window.location.pathname);

    const addUrl = Theme?.routes?.cart_add_url || '/cart/add.js';
    const addResponse = await fetch(addUrl, fetchConfig('javascript', { body: formData }));
    const addData = await addResponse.json();

    if (addData?.status) {
      throw new Error(addData?.message || 'Could not add upsell product to cart');
    }

    const cart = await this.#fetchCart();
    this.#syncCheckedStateFromCart(cart);
    this.#dispatchCartUpdate(cart, addData?.sections);
  }

  async #removeFromCart() {
    const sections = this.#getSectionIds();
    const body = JSON.stringify({
      id: this.#variantId,
      quantity: 0,
      sections,
      sections_url: window.location.pathname,
    });

    const changeUrl = Theme?.routes?.cart_change_url || '/cart/change.js';
    const response = await fetch(changeUrl, fetchConfig('json', { body }));
    const data = await response.json();

    if (data?.errors) {
      throw new Error(typeof data.errors === 'string' ? data.errors : 'Could not remove upsell product from cart');
    }

    this.#syncCheckedStateFromCart(data);
    this.#dispatchCartUpdate(data, data?.sections);
  }

  async #changeQuantity(newQty) {
    if (!this.#variantId || this.#pending) return;

    // Optimistic update
    this.#quantity = newQty;
    this.#updateQuantityDisplay();
    this.#setPending(true);

    try {
      const sections = this.#getSectionIds();
      const body = JSON.stringify({
        id: this.#variantId,
        quantity: newQty,
        sections,
        sections_url: window.location.pathname,
      });

      const changeUrl = Theme?.routes?.cart_change_url || '/cart/change.js';
      const response = await fetch(changeUrl, fetchConfig('json', { body }));
      const data = await response.json();

      if (data?.errors) {
        throw new Error(typeof data.errors === 'string' ? data.errors : 'Could not update quantity');
      }

      this.#syncCheckedStateFromCart(data);
      this.#dispatchCartUpdate(data, data?.sections);
    } catch (error) {
      console.error('Upsell quantity update failed:', error);
      await this.#syncFromCart();
    } finally {
      this.#setPending(false);
    }
  }

  async #syncFromCart() {
    if (!this.#variantId) return;

    try {
      const cart = await this.#fetchCart();
      this.#syncCheckedStateFromCart(cart);
    } catch (error) {
      console.error('Could not sync upsell checkbox state:', error);
    }
  }

  async #fetchCart() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Cart request failed with status ${response.status}`);
    }

    return response.json();
  }

  /** @param {{items?: Array<{variant_id: number | string, quantity: number}>}} cart */
  #syncCheckedStateFromCart(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const item = items.find((item) => String(item.variant_id) === this.#variantId);
    const isInCart = item != null && Number(item.quantity) > 0;

    // Don't overwrite quantity if user has pending taps in the debounce queue
    if (!this.#quantityDebounceTimer) {
      if (isInCart) {
        this.#quantity = Number(item.quantity);
      } else {
        this.#quantity = 1;
      }
      this.#updateQuantityDisplay();
    }

    this.#setChecked(isInCart);
  }

  #updateQuantityDisplay() {
    const qtyEl = this.querySelector('.product-upsell-item__qty-value');
    if (qtyEl) qtyEl.textContent = String(this.#quantity);
  }

  #setChecked(checked) {
    this.classList.toggle('product-upsell-item--selected', checked);
    this.setAttribute('aria-checked', checked ? 'true' : 'false');
  }

  #isChecked() {
    return this.classList.contains('product-upsell-item--selected');
  }

  #setPending(isPending) {
    this.#pending = isPending;
    this.setAttribute('aria-busy', isPending ? 'true' : 'false');
  }

  #getSectionIds() {
    const sectionIds = new Set();

    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    });

    return Array.from(sectionIds).join(',');
  }

  /** @param {Record<string, string> | undefined} sections */
  #dispatchCartUpdate(cart, sections) {
    const detail = {
      resource: cart || {},
      sourceId: this.#sourceId,
      data: {
        source: 'product-upsell-item',
        sections,
        itemCount: cart?.item_count ?? cart?.itemCount ?? 0,
        variantId: this.#variantId,
      },
    };

    document.dispatchEvent(
      new CustomEvent(ThemeEvents.cartUpdate, {
        bubbles: true,
        detail,
      })
    );
  }
}

if (!customElements.get('product-upsell-item-component')) {
  customElements.define('product-upsell-item-component', ProductUpsellItemComponent);
}
